/* eslint no-alert: 0 */
/* eslint no-console: 0 */
/* eslint no-mixed-operators: [2, {"allowSamePrecedence": true}] */
/* eslint no-cond-assign: 0 */
/* eslint no-else-return: 0 */
/* eslint no-restricted-syntax: 0 */
/* eslint no-underscore-dangle: 0 */

/*
 * EDITING THIS?
 * Please clean up UNSAFE_componentWillReceiveProps!
 * See: https://github.com/Tettra/tettra-wiki/issues/6002
 */

// External libraries
import React, { Component, Fragment } from 'react';
import {
    AtomicBlockUtils,
    Editor,
    EditorState,
    Modifier,
    RichUtils,
    SelectionState,
    getDefaultKeyBinding,
    getVisibleSelectionRect,
} from 'draft-js';
import Immutable from 'immutable';
import get from 'lodash/get';
import adjustBlockDepthForContentState from 'draft-js/lib/adjustBlockDepthForContentState';
import keyCommandInsertNewline from 'draft-js/lib/keyCommandInsertNewline';
import times from 'lodash/times';
import throttle from 'lodash/throttle';

import EditorAutosave from './EditorAutosave';
import Suggestions from './suggestions';
import { EditorStateProvider } from './EditorStateContext';
import contentToEditorState from '../utilities/contentToEditorState';
import convertFromHtml from '../utilities/convertHtmlToDraft';
import emptyEditorState from '../utilities/createEmptyEditorState';

// Copy Pasting
import {
    registerCopySource, handleDraftEditorPastedText,
} from './copyPaste';

// Custom Rendering
import blockRenderer from './blockRenderer';
import blockRenderMap from './blockRenderMap';

// Components
import Debug from './Debug';
import EmbedlyDialog from './EmbedlyDialog';
import FilePicker from './FilePicker';
import HighlightControls from './HighlightControls';
import InsertControls from './InsertControls';
import LinkDialog from './LinkDialog';
import ChoosePageOrLinkDialogWrapper from './ChoosePageOrLinkDialogWrapper';

// Extensions
import AutoMarkdown from '../extensions/AutoMarkdown/AutoMarkdown';
import styleMap from '../extensions/styleMap';

// Utilities
import {
    getCurrentBlock,
    getCurrentBlockTextUntilCursor,
    insertBlockAfter,
    insertBlockAfterContent,
    insertEmptyBlockAtEnd,
    selectAtomicBlock,
    shouldExitList,
    replaceWithBlock,
    shouldUnindent,
    insertHrBlock,
    shouldHidePlaceholder,
} from '../utilities/blockUtilities';
import {
    hasTableInSelection,
    insertTable,
} from '../utilities/tableUtilities';
import changeIndent from '../utilities/changeIndent';
import dragToUpload from '../utilities/dragUtilities';
import getEmbed from '../utilities/embedlyUtilities';
import {
    isTettraLink,
    getTettraPage,
    pasteInsideList,
    removeTabsFromText,
} from '../utilities/pasteUtilities';
import {
    moveSelectionToStart,
    getSearchText,
} from '../utilities/selectionUtilities';
import customKeyCommands from '../extensions/customKeyCommands';
import {
    BLOCK_TYPE,
    COMMAND,
    ENTITY_TYPE,
    FONT_ICON,
    KEY,
    KEYCODE,
    IMMUTABLE,
    MUTABLE,
    HANDLED,
    DOC_WIDTH,
    NOT_HANDLED,
    TAB_CHARACTER,
    STYLE_AUTO,
    INLINE_REFERENCE_TYPE,
    SUGGESTION_TRIGGERS,
    STYLE,
} from '../utilities/CONSTANTS';
import {
    importFileToEditorState,
    isImage,
    isSupportedSize,
    shouldImportFile,
    uploadFile,
    uploadBase64Image,
    uploadExternalImage,
    FILE_SIZE_MB,
} from '../utilities/fileUtilities';
import getUnixTime from '../utilities/timeUtilities';
import getConfig from '../utilities/configUtilities';
import shouldShowInsertButton from '../utilities/controlUtilities';
import handleInlineStyle from '../utilities/inlineStyleUtilities';
import { getGoogleDriveIcon } from '../../utilities/inlineReferenceUtilities';

import setIntegrationForUser from '../../utilities/integrationUtilities';
import { EVENTS } from '../../utilities/CONSTANTS';

export class TettraEditor extends Component {
    constructor(props) {
        super(props);

        this.config = getConfig(props.config);

        this.state = {
            linkDialog: false,
            embedlyDialog: false,
            spellCheck: this.config.spellCheck,
        };

        this.keyBindingCallbacks = [this.onMoveCursor];
    }

    componentDidMount() {
        const { isNestedEditor } = this.props;

        if (!isNestedEditor && !this.config.readOnly && this.config.enableDragToUpload) {
            dragToUpload(this.handleFiles);
            this.throttledForceUpdate = throttle(() => this.forceUpdate(), 200);
            window.addEventListener('resize', this.throttledForceUpdate);
        }

        if (this.config.enableExternalFocus) {
            window.addEventListener(EVENTS.FOCUS_EDITOR, this.setFocusFromOutside);
        }
        // If we don't use native event listeners, the setFocus function will
        // not work as intended unfortunately

        setIntegrationForUser(window.teamId, window.userId, this.config);

        // handling copy pasting
        this.copySource = registerCopySource(this.editor, this.onChange);
    }

    shouldComponentUpdate(nextProps) {
        const { activeEditor, editorState, readOnly } = this.props;

        if (nextProps.locationKey === 'root' || nextProps.locationKey === nextProps.activeEditor) {
            return true;
        }

        if (nextProps.activeEditor !== activeEditor) {
            return true;
        }

        if (nextProps.readOnly === false) {
            return true;
        }

        if (nextProps.readOnly !== readOnly) {
            return true;
        }

        return nextProps.editorState.getCurrentContent() !== editorState.getCurrentContent();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.throttledForceUpdate);
        if (this.config.enableExternalFocus) {
            window.removeEventListener(EVENTS.FOCUS_EDITOR, this.setFocusFromOutside);
        }
        if (this.copySource) {
            this.copySource.unregister();
        }
    }

    triggerSuggestion = (editorState) => {
        const selection = editorState.getSelection();
        const content = editorState.getCurrentContent();
        const currentBlock = content.getBlockForKey(selection.getStartKey());
        if (currentBlock == null || [BLOCK_TYPE.CODE].includes(currentBlock.getType())) return;

        const lastWord = currentBlock
            .getText()
            .slice(0, selection.getStartOffset())
            .split(' ')
            .slice(-1)[0];

        // check if current block is allowed,
        // check if there is no current entity
        const currentEntityKey = currentBlock.getEntityAt(selection.getStartOffset() - 1);

        const suggestionTriggerSymbols = [];
        if (this.config.enableMentionSuggestions) {
            suggestionTriggerSymbols.push(SUGGESTION_TRIGGERS.MENTION);
        }

        if (this.config.enableInternalSuggestions) {
            suggestionTriggerSymbols.push(SUGGESTION_TRIGGERS.INTERNAL);
        }

        if (this.config.enableExternalSuggestions) {
            suggestionTriggerSymbols.push(SUGGESTION_TRIGGERS.EXTERNAL);
        }

        if (currentEntityKey == null && suggestionTriggerSymbols.includes(lastWord[0])) {
            this.setState({
                suggestionTrigger: lastWord[0],
                searchText: lastWord.slice(1),
            });
        } else {
            this.setState({ suggestionTrigger: null, searchText: '' });
        }
    };

    /*
     * the additional location argument helps us track the origin of the
     * update, used for orchestrating undo state between nested and container editors
     */
    onChange = (editorState, location = 'root') => {
        const { onChange } = this.props;

        this.triggerSuggestion(editorState);

        if (onChange) {
            onChange(editorState, location);
        }
    };

    // eslint-disable-next-line
    onTab = (e) => {
        const {
            editorState,
            tableKey,
            columnNumber,
            insertRowAfter,
            rowNumber,
            isNestedEditor,
        } = this.props;
        if (this.config.enableSmallEditor && this.config.disableTabInSmallEditor) {
            return;
        }
        e.preventDefault();

        const command = this.keyBindingCallbacks.find(callback => callback(e));
        const $table = $(`table[data-table-id="${tableKey}"]`);

        if (command) {
            return this.handleKeyCommand(command);
        }

        const blockType = RichUtils.getCurrentBlockType(editorState);
        const selection = editorState.getSelection();

        // if we're inside a table switch to next cell
        if (isNestedEditor && selection.isCollapsed()) {
            if (!e.shiftKey) {
                const rows = $table.find('tr');
                const row = rows[rowNumber + 1];
                const cells = row.children;

                if (cells.length - 2 <= columnNumber) {
                    if ($table.find('tr').length - 2 <= rowNumber) {
                        insertRowAfter(rowNumber);
                        setTimeout(() => {
                            $($table.find('tr')[rowNumber + 2])
                                .find('td:eq(1) .nested-focus-toggle').click();
                        }, 100);
                    } else {
                        $($table
                            .find('tr')[rowNumber + 2])
                            .find('td:eq(1) .nested-focus-toggle').click();
                    }
                }

                $($(row).find('td')[columnNumber + 2]).find('.nested-focus-toggle').click();
            } else if (rowNumber === 0 && columnNumber === 0) {
                $table.find('tr:last-child() td:last-child() .nested-focus-toggle').click();
            } else if (columnNumber === 0) {
                $($table.find('tr')[rowNumber]).find('td:last-child() .nested-focus-toggle').click();
            } else {
                $($($table.find('tr')[rowNumber + 1]).find('td')[columnNumber]).find('.nested-focus-toggle').click();
            }
        // if we're on the first line of the editor and press shift + tab,
        // focus back on the title input
        } else if (selection.isCollapsed()
            && editorState.getCurrentContent()
                .getBlockBefore(selection.getStartKey()) == null
            && e.shiftKey
            && blockType !== BLOCK_TYPE.CODE
        ) {
            $('#post-editor-title').focus();
        } else if (blockType.includes(BLOCK_TYPE.ORDERED_LIST)) {
            this.onChange(
                EditorState.push(
                    editorState,
                    adjustBlockDepthForContentState(
                        editorState.getCurrentContent(),
                        selection,
                        e.shiftKey ? -1 : 1,
                        4,
                    ),
                    'adjust-depth',
                ),
            );
        } else if (selection.isCollapsed()
            && [BLOCK_TYPE.CODE, BLOCK_TYPE.UNSTYLED].includes(blockType)
            && !e.shiftKey
        ) {
            this.onChange(EditorState.push(
                editorState,
                Modifier.insertText(
                    editorState.getCurrentContent(),
                    selection,
                    TAB_CHARACTER,
                ),
            ));
        } else {
            this.onChange(changeIndent(
                editorState,
                TAB_CHARACTER,
                /*
                 * Although changeIndent works for all types of editable
                 * blocks (except lists, they have their own handling),
                 * we only use it for code blocks and unstyled blocks
                 * right now
                 */
                block => [BLOCK_TYPE.UNSTYLED, BLOCK_TYPE.CODE]
                    .includes(block.getType()),
                e.shiftKey,
            ));
        }
    };

    onMoveCursor = (e) => {
        const { editorState, onChange } = this.props;
        const content = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const currentKey = e.key === 'ArrowUp'
            ? selection.getStartKey()
            : selection.getEndKey();
        const nextKey = e.key === 'ArrowUp'
            ? content.getKeyBefore(currentKey)
            : content.getKeyAfter(currentKey);
        const currentBlock = content.getBlockForKey(currentKey);
        const nextBlock = content.getBlockForKey(nextKey);

        if (nextBlock != null && nextBlock.getType() !== BLOCK_TYPE.ATOMIC && currentBlock.getType() !== BLOCK_TYPE.ATOMIC) {
            return undefined;
        // we only need to interfere with default behaviour if
        // the next block is atomic, otherwise default behaviour is OK
        } else if (e.shiftKey !== true && nextBlock != null && ['ArrowDown', 'ArrowUp'].includes(e.key)) {
            // if current block is atomic we move selection to next block
            let moveToNext = currentBlock.getText().length === 0 || currentBlock.getType() === BLOCK_TYPE.ATOMIC;
            const domSelection = document.getSelection();
            const { baseNode } = domSelection;

            if (moveToNext === false) {
                // if current block is text we need to check if we're on
                // the last line before we move the selection
                const selectionRect = getVisibleSelectionRect(window);
                const blockNode = $(baseNode).closest('.public-DraftStyleDefault-block')[0];
                const { lineHeight } = window.getComputedStyle(blockNode);
                const blockRect = blockNode.getBoundingClientRect();
                const remainder = e.key === 'ArrowUp'
                    ? selectionRect.top - blockRect.top
                    : blockRect.bottom - selectionRect.bottom;
                moveToNext = Math.floor(remainder / parseInt(lineHeight, 10)) <= 0;
            }

            if (moveToNext === true) {
                e.preventDefault();
                if (nextBlock.getType() === BLOCK_TYPE.ATOMIC) {
                    domSelection.removeAllRanges();
                }
                onChange(
                    EditorState.forceSelection(
                        editorState,
                        selection.merge({
                            anchorOffset: 0,
                            anchorKey: nextKey,
                            focusOffset: 0,
                            focusKey: nextKey,
                        }),
                    ),
                );
                setTimeout(this.scrollIntoView);
                return HANDLED;
            }
        }

        return undefined;
    };

    scrollIntoView = () => {
        const { editorState } = this.props;
        const start = editorState.getSelection().getStartKey();
        const blockNode = document.querySelector(`[data-offset-key="${start}-0-0"]`);

        const { top: elTop, height: elHeight } = blockNode.getBoundingClientRect();
        const navHeight = 55;

        // is element below
        if ((window.pageYOffset + window.innerHeight) < (window.pageYOffset + elTop + elHeight)) {
            const difference = (window.pageYOffset + elTop + elHeight) - (window.pageYOffset + window.innerHeight);
            window.scrollTo(0, window.pageYOffset + difference);
        // or is it avove (o_O)
        } else if (window.pageYOffset + navHeight > (window.pageYOffset + elTop)) {
            const difference = window.pageYOffset - (window.pageYOffset + elTop);
            window.scrollTo(0, window.pageYOffset - difference - navHeight);
        }
    };

    getHighlightControlPosition = () => {
        let position;
        const selectionRect = getVisibleSelectionRect(window);

        if (selectionRect) {
            const { width } = selectionRect;
            const left = selectionRect.left + window.scrollX + (width / 2);
            position = {
                top: (selectionRect.top + window.scrollY) - 40,
                left,
                transform: 'translate(-50%) scale(1)',
                transition: 'transform 0.15s cubic-bezier(.3,1.2,.2,1)',
            };
            this.lastHighlightControlPosition = position;
        }

        return position;
    };

    // Only meant for using from outside
    setFocusFromOutside = (event, isEnter) => {
        const {
            locationKey,
            setActiveEditor,
            readOnly,
            editorState,
            onChange,
        } = this.props;
        const selection = editorState.getSelection();
        let newEditorState = editorState;

        if (!selection.getHasFocus()) {
            if (readOnly === true) {
                setActiveEditor(locationKey);
            }
            this.editor.focus();
        }

        if (isEnter === true) {
            newEditorState = keyCommandInsertNewline(
                moveSelectionToStart(editorState),
            );
        }

        setActiveEditor(locationKey);

        onChange(moveSelectionToStart(newEditorState));

        setTimeout(() => {
            this.editor.focus();
        });
    };

    handleBottomEditorClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        let { editorState } = this.props;
        const { locationKey, setActiveEditor } = this.props;
        let currentContent = editorState.getCurrentContent();
        let lastBlock = currentContent.getLastBlock();

        if (lastBlock.getType() === BLOCK_TYPE.ATOMIC) {
            editorState = insertEmptyBlockAtEnd(editorState);
        }

        currentContent = editorState.getCurrentContent();
        lastBlock = currentContent.getLastBlock();
        const lastBlockKey = lastBlock.getKey();
        const focusOffset = lastBlock.getText().length || 0;

        const newSelection = SelectionState.createEmpty(lastBlockKey)
            .merge({
                anchorKey: lastBlockKey,
                anchorOffset: focusOffset,
                focusOffset,
            });

        setActiveEditor(locationKey);
        this.onChange(EditorState.forceSelection(editorState, newSelection));
    };

    getEditorState = () => {
        if (this.props) {
            const { editorState } = this.props;
            return editorState;
        }

        return null;
    };

    updateTableCell = (blockKey, rowNum, colNum, newCellState) => {
        const { editorState } = this.props;
        const content = editorState.getCurrentContent();
        const block = content.getBlockForKey(blockKey);
        let rows = block.getIn(['data', 'rows'], []);
        const locationKey = `${blockKey}-${rowNum}-${colNum}`;
        const cellState = get(rows, [rowNum, colNum]);
        if (cellState == null) return;

        rows = rows.map((row, _rowNum) => (
            _rowNum === rowNum
                ? row.map((col, _colNum) => (
                    _colNum === colNum
                        ? newCellState
                        : col
                ))
                : row
        ));

        const newContent = Modifier.setBlockData(
            content,
            editorState.getSelection().merge({
                anchorOffset: 0,
                focusOffset: 0,
                anchorKey: blockKey,
                focusKey: blockKey,
            }),
            Immutable.Map({ rows }),
        );

        if (cellState.getCurrentContent() !== newCellState.getCurrentContent()) {
            this.onChange(EditorState.push(
                editorState,
                newContent,
                newCellState.getLastChangeType(),
            ), locationKey);
        } else {
            this.onChange(
                EditorState.set(
                    editorState,
                    {
                        currentContent: newContent.merge({
                            selectionBefore: content.getSelectionBefore(),
                        }),
                        lastChangeType: null,
                    },
                ),
                locationKey,
            );
        }
    };

    updateTableRows = (blockKey, rows) => {
        const { editorState } = this.props;
        const currentActive = $(document.activeElement).closest('[data-location-key]').data('location-key');

        this.onChange(EditorState.push(
            editorState,
            Modifier.setBlockData(
                editorState.getCurrentContent(),
                editorState.getSelection().merge({
                    anchorOffset: 0,
                    focusOffset: 0,
                    anchorKey: blockKey,
                    focusKey: blockKey,
                }),
                Immutable.Map({ rows }),
            ),
            'change-block-data',
        ), currentActive);

        setTimeout(() => this.retainFocus());
    };

    insertRowAfter = (blockKey, rowNum) => {
        const { editorState } = this.props;
        const content = editorState.getCurrentContent();
        const block = content.getBlockForKey(blockKey);
        let rows = block.getIn(['data', 'rows']);
        const rowLength = rows[0].length;
        const firstHalf = rows.slice(0, rowNum + 1);
        const secondHalf = rows.slice(rowNum + 1);
        rows = [...firstHalf, times(rowLength, () => emptyEditorState()), ...secondHalf];
        const locationKey = `${blockKey}-${rowNum}-0`;
        this.updateTableRows(blockKey, rows, locationKey);
    };

    insertColumnAfter = (blockKey, colNum) => {
        const { editorState } = this.props;
        const content = editorState.getCurrentContent();
        const block = content.getBlockForKey(blockKey);
        let rows = block.getIn(['data', 'rows']);

        rows = rows.map((row) => {
            const start = row.slice(0, colNum + 1);
            const end = row.slice(colNum + 1);
            return [...start, emptyEditorState(), ...end];
        });

        this.updateTableRows(blockKey, rows);
    };

    insertRowBefore = (entityKey, rowNum) => {
        this.insertRowAfter(entityKey, rowNum - 1);
    };

    insertColumnBefore = (entityKey, colNum) => {
        this.insertColumnAfter(entityKey, colNum - 1);
    };

    deleteColumn = (blockKey, colNum) => {
        const { editorState } = this.props;
        const content = editorState.getCurrentContent();
        const block = content.getBlockForKey(blockKey);
        let rows = block.getIn(['data', 'rows']);
        rows = rows.map(row => row.slice(0, colNum).concat(row.slice(colNum + 1)));
        this.updateTableRows(blockKey, rows);
    };

    deleteRow = (blockKey, rowNum) => {
        const { editorState } = this.props;
        const content = editorState.getCurrentContent();
        const block = content.getBlockForKey(blockKey);
        let rows = block.getIn(['data', 'rows']);
        rows = rows.slice(0, rowNum).concat(rows.slice(rowNum + 1));
        this.updateTableRows(blockKey, rows);
    };

    /*
     * This retains focus if lost during an undo/redo event for example
     */
    retainFocus = () => {
        const { lastChangeLocation } = this.props;

        const currentActive = $(document.activeElement).closest('[data-location-key]').data('location-key');

        if (currentActive !== lastChangeLocation) {
            $(`[data-location-key="${lastChangeLocation}"] .focus-trigger`).first().click();
        }
    };

    undo = () => {
        const { editorState } = this.props;
        const undid = EditorState.undo(editorState);
        this.onChange(undid);
        setTimeout(this.retainFocus);
    };

    redo = () => {
        const { editorState } = this.props;
        this.onChange(EditorState.redo(editorState));
        setTimeout(this.retainFocus);
    };

    handleBeforeInput = (character) => {
        const { editorState } = this.props;
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const focusOffset = selection.getAnchorOffset();
        const block = getCurrentBlock(editorState);
        const blockType = RichUtils.getCurrentBlockType(editorState);
        const lastEntityKey = block.getEntityAt(focusOffset - 1);
        const lastEntityType = lastEntityKey ? contentState.getEntity(lastEntityKey).getType() : null;
        const currentEntityKey = block.getEntityAt(focusOffset);

        /*
         * If we're inside a link and the next character is not of the link entity
         * we end the range of the link entity by inserting the character as plain
         * text
         */
        if (lastEntityType === ENTITY_TYPE.LINK && lastEntityKey !== currentEntityKey) {
            this.onChange(
                EditorState.push(
                    editorState,
                    Modifier.insertText(
                        contentState,
                        selection,
                        character,
                    ),
                ),
            );

            return HANDLED;
        // eslint-disable-next-line
        } else if (character !== ' ') {
            return NOT_HANDLED;
        }

        const editorStateWithSpace = EditorState.push(
            editorState,
            Modifier.insertText(
                contentState,
                selection,
                character,
            ),
            'insert-characters',
        );

        if (blockType !== BLOCK_TYPE.CODE) {
            const editorStateWithInlineStyle = handleInlineStyle(editorStateWithSpace, character);
            if (editorStateWithInlineStyle !== editorStateWithSpace) {
                this.onChange(editorStateWithInlineStyle);
                return HANDLED;
            }
        }

        return NOT_HANDLED;
    };

    forceSelection = (selection) => {
        let { editorState } = this.props;
        const { onChange } = this.props;
        editorState = EditorState.forceSelection(editorState, selection || editorState.getSelection());
        onChange(editorState);
    };

    addKeyBindingCallback = (method) => {
        this.keyBindingCallbacks.push(method);
    };

    removeKeyBindingCallback = (method) => {
        const index = this.keyBindingCallbacks.indexOf(method);
        if (index > -1) {
            this.keyBindingCallbacks.splice(index, 1);
        }
    };

    keyBindingFn = (e) => {
        const {
            editorState,
            undo = this.undo,
            redo = this.redo,
        } = this.props;
        const blockType = RichUtils.getCurrentBlockType(editorState);
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const currentBlock = contentState.getBlockForKey(selection.getStartKey());
        const prevBlock = contentState.getBlockBefore(selection.getStartKey());
        const prevBlockType = prevBlock && prevBlock.getType();

        let command;

        if (e.metaKey && e.key === 'z') {
            if (e.shiftKey) {
                redo();
            } else {
                undo();
            }

            e.preventDefault();
            return null;
        }

        if (e.key === KEY.tab) {
            return this.onTab(e);
        }

        if ([KEYCODE.upArrow, KEYCODE.downArrow, KEYCODE.escape].includes(e.which)) {
            return this.keyBindingCallbacks.forEach(callback => callback(e));
        }

        for (const callback of this.keyBindingCallbacks) {
            if (command = callback(e)) return command;
        }

        if (blockType !== BLOCK_TYPE.CODE && !blockType.includes('header')) {
            command = AutoMarkdown.keyBindingFn(e, editorState);
        }

        if (e.key === KEY.backspace
            && selection.isCollapsed()
            && prevBlockType === BLOCK_TYPE.ATOMIC
            && currentBlock.getText().trim().length === 0
        ) {
            const newEditorState = EditorState.push(editorState, Modifier.removeRange(
                contentState,
                editorState.getSelection().merge({
                    anchorOffset: prevBlock.getText().length,
                    anchorKey: prevBlock.getKey(),
                    focusOffset: currentBlock.getText().length,
                }),
            ));

            selectAtomicBlock(prevBlock, this.onChange, newEditorState);

            return false;
        } else if (e.key === KEY.backspace
            && selection.isCollapsed()
            && currentBlock.getType() === BLOCK_TYPE.ATOMIC
        ) {
            this.onChange(
                EditorState.push(
                    editorState,
                    replaceWithBlock(contentState, selection),
                ),
            );

            return false;
        }

        if (currentBlock.getText().trim().length > 0
            && BLOCK_TYPE.ATOMIC === prevBlockType
            && selection.isCollapsed()
            && selection.getStartOffset() === 0
            && e.key === KEY.backspace
        ) {
            command = COMMAND.moveSelectionUp;
        } else if (blockType.includes('list') && selection.isCollapsed() && selection.getStartOffset() === 0 && e.key === 'Backspace') {
            command = 'toggle-block-type';
        }

        command = command || customKeyCommands(e, editorState);

        if (command) return command;

        command = getDefaultKeyBinding(e);

        return command;
    };

    handleKeyCommand = (command) => {
        const { editorState } = this.props;
        const selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        const currentBlock = contentState.getBlockForKey(selection.getStartKey());
        const prevBlock = contentState.getBlockBefore(selection.getStartKey());

        const lastLine = currentBlock.getText().slice(0, selection.getStartOffset()).split('\n').slice(-1)[0];
        const reg = /^\s+$/gi;
        const singleTabReg = new RegExp(TAB_CHARACTER, 'gi');

        if (selection.isCollapsed()
            && currentBlock.getType() === BLOCK_TYPE.CODE
            && reg.test(lastLine)
            && COMMAND.backspace === command
        ) {
            const tabsMatch = lastLine.match(singleTabReg);
            let numberOfTabs = tabsMatch != null ? tabsMatch.length : 0;
            if (numberOfTabs > 0 && (numberOfTabs * TAB_CHARACTER.length) === lastLine.length) {
                numberOfTabs -= 1;
            }

            this.onChange(EditorState.push(
                editorState,
                Modifier.removeRange(
                    contentState,
                    selection.merge({
                        anchorOffset: selection.getStartOffset() - lastLine.length
                            + (numberOfTabs * TAB_CHARACTER.length),
                    }),
                ),
                'remove-range',
            ));
            return HANDLED;
        }

        if (command === COMMAND.toggleBlockType) {
            this.onChange(RichUtils.toggleBlockType(editorState, RichUtils.getCurrentBlockType(editorState)));
            return HANDLED;
        }

        if (command === COMMAND.toggleHeaderOne) {
            this.onChange(RichUtils.toggleBlockType(editorState, BLOCK_TYPE.H1));
            return HANDLED;
        }

        if (command === COMMAND.toggleHeaderTwo) {
            this.onChange(RichUtils.toggleBlockType(editorState, BLOCK_TYPE.H2));
            return HANDLED;
        }

        if (command === COMMAND.toggleHeaderThree) {
            this.onChange(RichUtils.toggleBlockType(editorState, BLOCK_TYPE.H3));
            return HANDLED;
        }

        if (command === COMMAND.toggleOrderedList) {
            this.onChange(RichUtils.toggleBlockType(editorState, BLOCK_TYPE.ORDERED_LIST));
            return HANDLED;
        }

        if (command === COMMAND.toggleUnorderedList) {
            this.onChange(RichUtils.toggleBlockType(editorState, BLOCK_TYPE.UNORDERED_LIST));
            return HANDLED;
        }

        if (command === COMMAND.toggleInlineFormatStrikethrough) {
            this.onChange(RichUtils.toggleInlineStyle(editorState, STYLE.STRIKETHROUGH));
            return HANDLED;
        }

        if (command === COMMAND.toggleInlineFormatHighlight) {
            this.onChange(RichUtils.toggleInlineStyle(editorState, STYLE.HIGHLIGHT));
            return HANDLED;
        }

        if (command === COMMAND.toggleInlineFormatCode) {
            this.onChange(RichUtils.toggleInlineStyle(editorState, STYLE.CODE));
            return HANDLED;
        }

        if (command === COMMAND.moveSelectionUp) {
            selectAtomicBlock(prevBlock, this.onChange, editorState);
            return HANDLED;
        }

        if (command === COMMAND.showLinkDialog) {
            this.toggleLinkDialog(true);
            return HANDLED;
        }

        let newState = RichUtils.handleKeyCommand(editorState, command);

        if (newState) {
            this.onChange(newState);
            return HANDLED;
        }

        newState = AutoMarkdown.handleKeyCommand(editorState, command);

        if (newState) {
            this.onChange(newState);
            return HANDLED;
        }

        return NOT_HANDLED;
    };

    handleFiles = (files) => {
        const file = files[0];

        if (shouldImportFile(file)) {
            const { editorState } = this.props;
            importFileToEditorState(file, editorState)
                .then(newEditorState => this.onChange(newEditorState))
                .catch(e => console.error(e));
        } else if (file && isImage(file) && this.config.enableImageUpload && isSupportedSize(file)) {
            this.uploadImage(file);
        } else if (file && !isImage(file) && this.config.enableFileUpload && isSupportedSize(file)) {
            this.uploadFile(file);
        } else if (isImage(file) && !this.config.enableImageUpload) {
            alert('Image upload is not supported');
        } else if (!this.config.enableFileUpload) {
            alert('File upload is not supported');
        } else {
            alert(`File is bigger than ${FILE_SIZE_MB}mb or not supported`);
        }
    };

    handleExternalandBase64Images = (initialEditorState) => {
        const originalContentState = initialEditorState.getCurrentContent();
        originalContentState.getBlockMap().forEach(async (block) => {
            const entityKey = block.getEntityAt(0);
            const entity = entityKey && originalContentState.getEntity(entityKey);
            const entityData = entity && entity.getData();
            if (entity && entity.type === ENTITY_TYPE.IMAGE && entityData.isExternalPastedImage && !entityData.externalUploadedImage) {
                let errorUploading = null;
                const imageExternalUrl = entity.data.src;
                const { image_url: src } = await uploadExternalImage(imageExternalUrl).catch(() => (errorUploading = true));
                const { editorState, onChange } = this.props;
                const contentState = editorState.getCurrentContent();

                if (!errorUploading && src) {
                    const externalUploadedImage = true;
                    contentState.mergeEntityData(entityKey, { src, externalUploadedImage });
                    onChange(EditorState.forceSelection(editorState, editorState.getSelection()));
                } else {
                    contentState.mergeEntityData(entityKey, { errorUploading });
                    onChange(EditorState.forceSelection(editorState, editorState.getSelection()));
                }
            } else if (entity && entity.type === ENTITY_TYPE.IMAGE && entityData.isBase64Image) {
                let errorUploading = null;
                const { image_url: src } = await uploadBase64Image(entity.data.src).catch(() => (errorUploading = true));
                const { editorState, onChange } = this.props;
                const contentState = editorState.getCurrentContent();
                if (!errorUploading && src) {
                    const externalUploadedImage = true;
                    contentState.mergeEntityData(entityKey, { src, externalUploadedImage });
                    onChange(EditorState.forceSelection(editorState, editorState.getSelection()));
                } else {
                    contentState.mergeEntityData(entityKey, { errorUploading });
                    onChange(EditorState.forceSelection(editorState, editorState.getSelection()));
                }
            }
        });
    };

    handlePastedText = (text, html) => {
        const { editorState } = this.props;
        const blockType = RichUtils.getCurrentBlockType(editorState);
        let contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const startBlockType = contentState.getBlockForKey(selection.getStartKey()).getType();
        const endBlockType = contentState.getBlockForKey(selection.getEndKey()).getType();
        const externalHtmlPaste = html != null && ![BLOCK_TYPE.CODE, BLOCK_TYPE.CALLOUT].includes(blockType);

        const internalPaste = handleDraftEditorPastedText(html, editorState);
        if (internalPaste) {
            this.onChange(internalPaste);
            return HANDLED;
        }

        if (text == null) {
            return NOT_HANDLED;
        } else if (externalHtmlPaste) {
            const pastedContentState = contentToEditorState(convertFromHtml(html)).getCurrentContent();
            contentState = Modifier.replaceWithFragment(contentState, selection, pastedContentState.getBlockMap());
        } else if (startBlockType.includes('list') && endBlockType.includes('list')) {
            contentState = pasteInsideList(contentState, selection, text, blockType);
        } else if ([BLOCK_TYPE.CODE, BLOCK_TYPE.CALLOUT].includes(blockType)) {
            const codeText = removeTabsFromText(text);
            contentState = Modifier.replaceText(
                contentState,
                selection,
                codeText,
            );
        } else if (isTettraLink(text)) {
            const page = getTettraPage(text);
            if (page) {
                const pasted = true;
                this.insertResourceInlineReference(page, pasted);
                return HANDLED;
            }
            return NOT_HANDLED;
        } else {
            return NOT_HANDLED;
        }

        let newEditorState = EditorState.push(editorState, contentState, 'insert-fragment');

        if (![BLOCK_TYPE.CODE, BLOCK_TYPE.CALLOUT].includes(blockType)) {
            newEditorState = handleInlineStyle(newEditorState, ' ');
        }

        this.onChange(newEditorState);

        if (externalHtmlPaste) {
            this.handleExternalandBase64Images(newEditorState);
        }

        return HANDLED;
    };

    handleReturn = (e) => {
        let command;
        this.keyBindingCallbacks.forEach((callback) => { command = callback(e); });

        if (command === COMMAND.insertSuggestion
            || command === COMMAND.insertMention
            || command === COMMAND.insertPageReference
            || command === COMMAND.insertGithubIssue
            || command === COMMAND.insertGoogleDriveDoc
        ) {
            return HANDLED;
        }

        let newEditorState;
        const { editorState } = this.props;
        const selection = editorState.getSelection();

        const contentState = editorState.getCurrentContent();
        const blockKey = selection.getStartKey();
        const block = contentState.getBlockForKey(blockKey);
        const blockType = block.getType();

        if (BLOCK_TYPE.ATOMIC === blockType) {
            newEditorState = insertBlockAfter(editorState, blockKey, 'unstyled', Immutable.Map());
        } else if ([BLOCK_TYPE.CALLOUT, BLOCK_TYPE.CODE].includes(blockType) && block.getText().substr(-1) === '\n' && selection.getFocusOffset() === block.getText().length) {
            // exit code block or callout
            const newLinesSelection = selection.merge({
                anchorOffset: selection.getFocusOffset() - 1,
            });

            let newContentState = Modifier.removeRange(
                contentState,
                newLinesSelection,
                'backward',
            );

            newContentState = insertBlockAfterContent(
                newContentState,
                selection,
                blockKey,
                BLOCK_TYPE.UNSTYLED,
                block.getData(),
            );

            newEditorState = EditorState.push(editorState, newContentState);
        } else if (blockType === BLOCK_TYPE.CODE) {
            // keep indent code block
            const lastLine = block.getText().slice(0, selection.getStartOffset()).split('\n').slice(-1)[0];
            const whiteSpace = lastLine.match(/^\s+/gi);
            let numberOfTabs = 0;

            if (whiteSpace != null) {
                const tabsMatch = whiteSpace[0].match(new RegExp(TAB_CHARACTER, 'gi'));
                numberOfTabs = tabsMatch != null ? tabsMatch.length : 0;
            }

            if (numberOfTabs > 0) {
                newEditorState = EditorState.push(
                    editorState,
                    Modifier.insertText(
                        contentState,
                        selection,
                        `\n${times(numberOfTabs, () => TAB_CHARACTER).join('')}`,
                    ),
                    'insert-characters',
                );
            } else {
                newEditorState = RichUtils.insertSoftNewline(editorState);
            }
        } else if (BLOCK_TYPE.CALLOUT === blockType) {
            newEditorState = RichUtils.insertSoftNewline(editorState);
        } else if (e.key === KEY.enter && e.shiftKey) {
            newEditorState = RichUtils.insertSoftNewline(editorState);
        } else if (shouldExitList(editorState) && selection.isCollapsed()) {
            newEditorState = EditorState.push(
                editorState,
                Modifier.setBlockType(
                    contentState,
                    selection,
                    BLOCK_TYPE.UNSTYLED,
                ),
            );
        } else if (shouldUnindent(editorState) && selection.isCollapsed()) {
            newEditorState = EditorState.push(
                editorState,
                adjustBlockDepthForContentState(contentState, selection, -1, 4),
                'adjust-depth',
            );
        // if we have any styles on the current line, reset them and insert newline
        } else if (editorState.getCurrentInlineStyle().size > 0) {
            newEditorState = EditorState.setInlineStyleOverride(keyCommandInsertNewline(editorState), Immutable.OrderedSet());
        } else if (
            !blockType.includes(BLOCK_TYPE.UNSTYLED)
            && !blockType.includes(BLOCK_TYPE.ORDERED_LIST)
            && getCurrentBlockTextUntilCursor(editorState)
        ) {
            newEditorState = RichUtils.toggleBlockType(keyCommandInsertNewline(editorState), BLOCK_TYPE.UNSTYLED);
        } else if (
            (blockType === BLOCK_TYPE.UNSTYLED || blockType.includes(BLOCK_TYPE.ORDERED_LIST))
            && selection.isCollapsed()
        ) {
            const editorStateWithSpace = EditorState.push(
                editorState,
                Modifier.insertText(
                    contentState,
                    selection,
                    ' ',
                ),
                'insert-characters',
            );

            const editorStateWithInlineStyle = handleInlineStyle(editorStateWithSpace, e.key);

            if (editorStateWithInlineStyle !== editorStateWithSpace) {
                newEditorState = keyCommandInsertNewline(editorStateWithInlineStyle);
            }
        }

        if (newEditorState) {
            this.onChange(newEditorState);
            return HANDLED;
        }
        return NOT_HANDLED;
    };

    toggleInlineStyle = (inlineStyle) => {
        const { editorState } = this.props;

        this.onChange(
            RichUtils.toggleInlineStyle(editorState, inlineStyle),
        );
    };

    focusTableInBlock = (blockKey) => {
        $(`table[data-table-id="${blockKey}"] tr:eq(1) td:eq(1) .nested-focus-toggle`).click();
    };

    toggleBlockType = (blockType) => {
        const { editorState, setActiveEditor, locationKey } = this.props;
        if (blockType === BLOCK_TYPE.TABLE) {
            const newEditorState = insertTable(editorState);
            const blockKey = newEditorState.getCurrentContent().getKeyAfter(editorState.getSelection().getStartKey());

            this.onChange(newEditorState);

            setActiveEditor(locationKey);
            setTimeout(() => this.focusTableInBlock(blockKey));
        } else if (blockType === BLOCK_TYPE.HR) {
            this.onChange(
                insertHrBlock(editorState),
            );
        } else {
            this.onChange(
                RichUtils.toggleBlockType(editorState, blockType),
            );
        }
    };

    selectLink = (entityKey, textLength) => {
        const { editorState } = this.props;
        const contentState = editorState.getCurrentContent();
        const selectionState = editorState.getSelection();
        const anchorKey = selectionState.getAnchorKey();
        const block = contentState.getBlockForKey(anchorKey);

        const start = block.getCharacterList().findIndex(ch => ch.get('entity') === entityKey);

        // if we don't wrap this in a set timeout the position will be wrong.
        setTimeout(() => {
            this.onChange(EditorState.forceSelection(
                editorState,
                editorState.getSelection()
                    .merge({
                        anchorOffset: start,
                        focusOffset: start + textLength,
                    }),
            ));
            this.toggleLinkDialog(true);
        });
    };

    toggleLinkDialog = (showLinkDialog) => {
        const { linkDialog } = this.state;

        this.setState({
            linkDialog: showLinkDialog != null ? showLinkDialog : !linkDialog,
        });
    };

    toggleEmbedlyDialog = (showEmbedlyDialog) => {
        const { embedlyDialog } = this.state;

        this.setState({
            embedlyDialog: showEmbedlyDialog != null ? showEmbedlyDialog : !embedlyDialog,
        });
    };

    uploadFile = (file) => {
        const fileKey = this.insertFile(file);
        const { editorState: originalEditorState, setUploading, onChange } = this.props;

        const contentState = originalEditorState.getCurrentContent();

        setUploading(true);

        uploadFile(file, this.config.fileApi)
            .then((fileResponse) => {
                const { editorState } = this.props;
                const src = fileResponse.url;
                const date = new Date();

                contentState.mergeEntityData(fileKey, { src, date });
                onChange(EditorState.forceSelection(editorState, editorState.getSelection()));
                setUploading(false);
            }).catch((error) => {
                console.error('file failed to upload', error);
                setUploading(false);

                Modifier.removeRange(
                    originalEditorState.getCurrentContent(),
                    originalEditorState.getSelection(),
                    'backward',
                );

                onChange(EditorState.forceSelection(originalEditorState, originalEditorState.getSelection()));

                alert('File upload error: please check your network connection and that the file type is supported (.pdf, .txt, Office Formats).');
            });
    };

    insertFile = (file) => {
        const { editorState, onChange } = this.props;
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(ENTITY_TYPE.FILE, IMMUTABLE, {
            name: file.name,
            type: file.type,
        });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        onChange(AtomicBlockUtils.insertAtomicBlock(
            editorState,
            entityKey,
            file.name,
        ));
        return entityKey;
    };

    uploadImage = (image) => {
        const { editorState, onChange, setUploading } = this.props;
        const tempImageKey = this.insertImage(this.config.imagePlaceholder, true);
        const originalEditorState = editorState;
        const contentState = originalEditorState.getCurrentContent();

        setUploading(true);

        uploadFile(image, this.config.fileApi)
            .then(({ url }) => {
                contentState.replaceEntityData(tempImageKey, {
                    src: url,
                    isPlaceholder: false,
                });
                setUploading(false);
                this.forceSelection();
            }).catch((error) => {
                setUploading(false);
                console.error('image failed to upload: ', error);

                Modifier.removeRange(
                    originalEditorState.getCurrentContent(),
                    originalEditorState.getSelection(),
                    'backward',
                );

                onChange(EditorState.forceSelection(originalEditorState, originalEditorState.getSelection()));

                alert('Image upload error: please check your network connection');
            });
    };

    insertImage = (url, isPlaceholder = false) => {
        const { editorState, onChange } = this.props;
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(ENTITY_TYPE.IMAGE, IMMUTABLE, { src: url, isPlaceholder });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        onChange(AtomicBlockUtils.insertAtomicBlock(
            editorState,
            entityKey,
            ' ',
        ));
        return entityKey;
    };

    selectImage = (blockKey) => {
        const { editorState } = this.props;
        const updateSelection = new SelectionState({
            anchorKey: blockKey,
            anchorOffset: 0,
            focusKey: blockKey,
            focusOffset: 0,
            isBackward: false,
        });
        const newEditorState = EditorState.forceSelection(editorState, editorState.getSelection().merge(updateSelection));
        this.onChange(newEditorState);
    };

    insertIframe = (url) => {
        const { editorState, onChange } = this.props;
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(ENTITY_TYPE.IFRAME, IMMUTABLE, { src: url });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        let newEditorState = AtomicBlockUtils.insertAtomicBlock(
            editorState,
            entityKey,
            ' ',
        );

        const blockKey = newEditorState.getCurrentContent().getBlockBefore(newEditorState.getSelection().getAnchorKey()).getKey();

        newEditorState = EditorState.push(
            newEditorState,
            Modifier.mergeBlockData(
                newEditorState.getCurrentContent(),
                editorState.getSelection().merge({
                    anchorKey: blockKey,
                    focusKey: blockKey,
                    anchorOffset: 0,
                    focusOffset: 0,
                }),
                Immutable.Map({ width: DOC_WIDTH }),
            ),
        );

        onChange(newEditorState);
        return entityKey;
    };

    insertEmbedly = (url) => {
        const { editorState, onChange, setUploading } = this.props;
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(ENTITY_TYPE.EMBEDLY, IMMUTABLE, { src: url });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        onChange(AtomicBlockUtils.insertAtomicBlock(
            editorState,
            entityKey,
            ' ',
        ));

        setUploading(true);

        getEmbed(url)
            .then((embedly) => {
                if (embedly.html && !embedly.html.includes('<iframe')) {
                    delete embedly.html;
                }

                if (embedly.width != null && embedly.width > DOC_WIDTH) {
                    embedly.height *= DOC_WIDTH / embedly.width;
                    embedly.width = DOC_WIDTH;
                }

                contentState.mergeEntityData(entityKey, { embedly });
                setUploading(false);
                this.forceSelection();
            }).catch((error) => {
                console.error('embed failed ', error);
                setUploading(false);

                Modifier.removeRange(
                    editorState.getCurrentContent(),
                    editorState.getSelection(),
                    'backward',
                );

                onChange(
                    EditorState.forceSelection(editorState, editorState.getSelection()),
                );
                alert('Embed failed: please check your network connection and ensure you entered a valid url');
            });
    };

    /**
     * Insert an inline reference entity (e.g., GitHub Issue or Page Reference) in the editor at the current selection
     * position.
     *
     * @param {Object} entityData
     * @param {string} entityData.referenceType The category/sub-type of inline reference. Must match an entry in
     *     `INLINE_REFERENCE_TYPE` constant
     * @param {string} entityData.title Text to be displayed in editor
     * @param {string} entityData.url URL to navigate to when reference is clicked
     * @param {string} [entityData.insertedAt] The UTC time this action was performed (will be set to current time if
     *     not included)
     * @param {string} icon The "Redstone" font icon character (see `FONT_ICON` in CONSTANTS.js)
     * @param {Boolean} [pasted] `True` if content is being pasted from clipboard
     */
    insertInlineReference = (entityData, icon, pasted = false) => {
        Object.assign(entityData, { insertedAt: getUnixTime() });
        let { editorState } = this.props;
        const contentState = editorState.getCurrentContent();
        const selectionState = editorState.getSelection();
        const contentStateWithEntity = contentState.createEntity(ENTITY_TYPE.INLINE_REFERENCE, IMMUTABLE, entityData);
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const { begin: anchorOffset, end: focusOffset } = getSearchText(editorState, selectionState);

        const inlineReferenceTextSelection = pasted ? selectionState : selectionState.merge({ anchorOffset, focusOffset });
        const entityText = `${icon}${entityData.title}`;
        const inlineReferenceReplacedContent = Modifier.replaceText(
            contentState,
            inlineReferenceTextSelection,
            entityText,
            null,
            entityKey,
        );

        const inlineReferenceIconSelection = selectionState.merge({ anchorOffset, focusOffset: anchorOffset + 1 });
        const inlineReferenceWithStyledIconContent = Modifier.applyInlineStyle(
            inlineReferenceReplacedContent,
            inlineReferenceIconSelection,
            STYLE_AUTO.FONT_ICON,
        );

        // TODO Extract utility function for performing actions in editor without including them in undo stack
        editorState = EditorState.set(editorState, { allowUndo: false });

        editorState = EditorState.push(editorState, inlineReferenceWithStyledIconContent);

        const offset = anchorOffset + entityText.length;
        const selectionStateAfterRefInserted = selectionState.merge({
            anchorOffset: offset,
            focusOffset: offset,
        });
        editorState = EditorState.forceSelection(editorState, selectionStateAfterRefInserted);
        editorState = EditorState.push(
            editorState,
            Modifier.insertText(
                editorState.getCurrentContent(),
                editorState.getSelection(),
                ' ',
            ),
            COMMAND.insertInlineReference,
        );

        editorState = EditorState.set(editorState, { allowUndo: true });

        this.onChange(editorState);
    };

    insertInlineReferenceFromDialog = (entityData, icon) => {
        Object.assign(entityData, { insertedAt: getUnixTime(), fromDialog: true });
        let { editorState } = this.props;
        const contentState = editorState.getCurrentContent();
        const selectionState = editorState.getSelection();
        const contentStateWithEntity = contentState.createEntity(ENTITY_TYPE.INLINE_REFERENCE, IMMUTABLE, entityData);
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const entityText = `${icon}${entityData.title}`;
        editorState = EditorState.push(editorState, Modifier.insertText(contentState, selectionState, entityText, undefined, entityKey));

        const anchorOffset = selectionState.getAnchorOffset();
        const inlineReferenceIconSelection = selectionState.merge({ anchorOffset, focusOffset: anchorOffset + 1 });
        const inlineReferenceWithStyledIconContent = Modifier.applyInlineStyle(
            editorState.getCurrentContent(),
            inlineReferenceIconSelection,
            STYLE_AUTO.FONT_ICON,
        );

        editorState = EditorState.push(editorState, inlineReferenceWithStyledIconContent);
        editorState = EditorState.set(editorState, { allowUndo: true });

        return this.onChange(editorState);
    };

    insertMention = (user, isPasted = false) => {
        const {
            id,
            url,
            email,
            display_name: displayName,
            first_name: firstName,
            image_192: image192,
            last_name: lastName,
            slack_id: slackId,
        } = user;

        this.insertInlineReference({
            referenceType: INLINE_REFERENCE_TYPE.MENTION,
            title: displayName,
            url,
            id,
            metadata: {
                email,
                firstName,
                image192,
                lastName,
                slackId,
            },
        }, '@', isPasted);
    };

    insertResourceInlineReference = (resource, isPasted = false, fromDialog = false) => {
        const entityData = resource.getEntityData();
        const icon = resource.getFontIcon();

        if (fromDialog) {
            return this.insertInlineReferenceFromDialog(entityData, icon);
        }

        this.insertInlineReference(entityData, icon, isPasted);
    };

    insertLink = (url, fromDialog = false) => {
        let { editorState } = this.props;
        const contentState = editorState.getCurrentContent();
        const selectionState = editorState.getSelection();
        const contentStateWithEntity = contentState.createEntity(ENTITY_TYPE.LINK, MUTABLE, { url, fromDialog });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        editorState = EditorState.push(editorState, Modifier.insertText(contentState, selectionState, url, undefined, entityKey));
        editorState = EditorState.set(editorState, { allowUndo: true });

        return this.onChange(editorState);
    };

    componentDidCatch() {
        this.forceUpdate();
    }

    render() {
        const {
            linkDialog,
            searchText,
            embedlyDialog,
            suggestionTrigger,
            spellCheck,
        } = this.state;

        const {
            editorState,
            setUploading,
            locationKey,
            isNestedEditor,
            activeEditor,
            readOnly,
            setActiveEditor,
            className,
        } = this.props;

        const tableMethods = {
            updateTable: this.updateTableCell,
            redo: this.redo,
            undo: this.undo,
            insertRowAfter: this.insertRowAfter,
            insertRowBefore: this.insertRowBefore,
            insertColumnAfter: this.insertColumnAfter,
            insertColumnBefore: this.insertColumnBefore,
            deleteRow: this.deleteRow,
            deleteColumn: this.deleteColumn,
        };

        const nestedEditorProps = {
            addChange: this.addChange,
            config: this.config,
            // setFocus: this.setFocus,
            activeEditor,
            setActiveEditor,
            setUploading,
        };

        const selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        const currentBlock = contentState.getBlockForKey(selection.getAnchorKey()) || Immutable.Map();
        const shouldShowHighlightControl = !readOnly
            && this.config.enableHighlightControls
            && !hasTableInSelection(editorState)
            && ![BLOCK_TYPE.CODE].includes(currentBlock.get('type'))
            && selection.getHasFocus()
            && !selection.isCollapsed()
            && !linkDialog;

        const showInsertButton = !isNestedEditor
            && this.config.enableInsertControls
            && $(document.activeElement).closest('.tettra-table').length < 1
            && selection.getHasFocus()
            && !readOnly
            && shouldShowInsertButton(selection, currentBlock);

        const editorContext = { activeEditor, editorState, selectLink: this.selectLink };
        const position = this.getHighlightControlPosition() || this.lastHighlightControlPosition;
        const placeholderText = (this.config.readOnly || isNestedEditor || shouldHidePlaceholder(editorState)) ? '' : this.config.textPlaceholder;
        let tettraEditorClass = 'tettra-editor';
        tettraEditorClass += !isNestedEditor ? ' root-editor' : '';
        tettraEditorClass += this.config.enableSmallEditor ? ' small-editor' : '';

        return (
            <div data-location-key={locationKey}>
                {!isNestedEditor && !this.config.readOnly && (
                    <Fragment>
                        <div
                            id="focus-trigger"
                            className="focus-trigger"
                            onClick={this.setFocusFromOutside}
                        />
                        <div
                            id="enter-trigger"
                            onClick={(e) => { this.setFocusFromOutside(e, true); }}
                        />
                    </Fragment>
                )}
                <div
                    ref={(element) => { this.container = element; }}
                    className={`editor-wrapper ${className}`}
                >
                    {isNestedEditor === true && !this.config.readOnly && (
                        <div
                            onClick={this.setFocusFromOutside}
                            className="focus-trigger nested-focus-toggle"
                        />
                    )}

                    {!readOnly && (
                        <HighlightControls
                            isOpen={shouldShowHighlightControl}
                            editorState={editorState}
                            toggleStyle={this.toggleInlineStyle}
                            toggleBlock={this.toggleBlockType}
                            toggleLinkDialog={this.toggleLinkDialog}
                        />
                    )}

                    {linkDialog && (
                        <LinkDialog
                            editorState={editorState}
                            setEditorState={this.onChange}
                            toggleLinkDialog={this.toggleLinkDialog}
                            position={position}
                        />
                    )}
                    {embedlyDialog && (
                        <EmbedlyDialog
                            insertEmbedly={this.insertEmbedly}
                            insertIframe={this.insertIframe}
                            toggleEmbedlyDialog={this.toggleEmbedlyDialog}
                            editorState={editorState}
                        />
                    )}
                    <FilePicker handleFiles={this.handleFiles} />
                    <div
                        className={tettraEditorClass}
                        onMouseDown={(e) => {
                            const container = $(e.target).closest('.editor-wrapper')[0];

                            if (isNestedEditor) {
                                e.stopPropagation();
                            }

                            if (container === this.container) {
                                setActiveEditor(locationKey);
                            }
                        }}
                    >
                        <EditorStateProvider
                            readOnly={this.config.readOnly}
                            value={editorContext}
                        >
                            <Editor
                                blockRendererFn={block => blockRenderer(block, contentState, tableMethods, nestedEditorProps, editorState, this.onChange, this.selectImage, readOnly, this.getEditorState, isNestedEditor)}
                                blockRenderMap={blockRenderMap(editorState)}
                                customStyleMap={styleMap}
                                editorState={editorState}
                                handleBeforeInput={this.handleBeforeInput}
                                handleKeyCommand={this.handleKeyCommand}
                                handlePastedFiles={this.handleFiles}
                                handlePastedText={this.handlePastedText}
                                handleReturn={this.handleReturn}
                                keyBindingFn={this.keyBindingFn}
                                onChange={this.onChange}
                                placeholder={placeholderText}
                                readOnly={readOnly}
                                ref={(element) => { this.editor = element; }}
                                spellCheck={spellCheck}
                            />
                        </EditorStateProvider>
                        {showInsertButton && (
                            <InsertControls
                                editorState={editorState}
                                config={this.config}
                                toggleBlockType={this.toggleBlockType}
                                toggleEmbedlyDialog={this.toggleEmbedlyDialog}
                            />
                        )}
                        {suggestionTrigger != null && (
                            <Suggestions
                                removeKeyBindingCallback={this.removeKeyBindingCallback}
                                addKeyBindingCallback={this.addKeyBindingCallback}
                                insertPageReference={this.insertResourceInlineReference}
                                insertExternalResourceEmbed={this.insertResourceInlineReference}
                                insertMention={this.insertMention}
                                trigger={suggestionTrigger}
                                editorState={editorState}
                                onChange={this.onChange}
                                searchText={searchText}
                            />
                        )}
                    </div>
                    {!isNestedEditor && !this.config.readOnly && !this.config.enableSmallEditor && (
                        <div className="revision-hide">
                            <div
                                id="bottom-click-container"
                                onMouseDown={this.handleBottomEditorClick}
                            />
                            <div id="drag-container">
                                <span className="icon icon-download" />
                                <p>Drop file to upload</p>
                            </div>
                        </div>
                    )}
                    {!isNestedEditor && !this.config.readOnly && this.config.enableChoosePageDialog && (
                        <ChoosePageOrLinkDialogWrapper
                            editorState={editorState}
                            onChange={this.onChange}
                            insertInlineReference={this.insertResourceInlineReference}
                            insertLink={this.insertLink}
                        />
                    )}
                    {this.config.debug && !isNestedEditor && <Debug editorState={editorState} />}
                </div>
            </div>
        );
    }
}

class EditorContainer extends Component {
    constructor(props) {
        super(props);

        /*
         * A little hack that lets us track when we're moving between cells or from cells
         * to root editor so that we can differentiate between those changes
         */
        this.editorHistory = [];
        this.editorHistoryIndex = 1;

        this.state = {
            editorState: props.editorState,
            activeEditor: props.config.readOnly === true ? 'none' : 'root',
        };
    }

    componentDidMount() {
        // this supresses the table cell switching error
        // necessary since we're doing something with draft js that we're not supposed to:
        // nesting draft js editors
        window.onerror = function ignoreDraftTableError(err) {
            return err.includes("Cannot read property 'getIn' of undefined");
        };
    }

    /*
     * EDITING THIS?
     * Please clean up UNSAFE_componentWillReceiveProps!
     * See: https://github.com/Tettra/tettra-wiki/issues/6002
     */
    UNSAFE_componentWillReceiveProps(props) {
        this.setState({
            editorState: props.editorState,
            activeEditor: props.config.readOnly === true ? 'none' : 'root',
        });
    }

    onChange = (editorState, location) => {
        const { updateModel } = this.props;
        const historySize = editorState.getUndoStack().size + editorState.getRedoStack().size;

        if (historySize > this.editorHistory.length) {
            this.editorHistory.push(location);
        } else if (historySize < this.editorHistory.length) {
            const end = historySize > 1 ? historySize - 2 : 0;
            this.editorHistory = this.editorHistory.slice(0, end);
            this.editorHistory.push(location);
        }

        this.editorHistoryIndex = editorState.getUndoStack().size - 1;
        updateModel(editorState);
        this.setState({ editorState });
    };

    setActiveEditor = (activeEditor) => {
        const { config } = this.props;
        if (config.readOnly !== true) {
            this.setState({ activeEditor });
        }
    };

    render() {
        const { editorState, activeEditor } = this.state;
        const { config: { readOnly, enableAutosave = true } } = this.props;

        if (editorState != null) {
            return (
                <Fragment>
                    <TettraEditor
                        {...this.props}
                        lastChangeLocation={this.editorHistory[this.editorHistoryIndex] || 'root'}
                        locationKey="root"
                        readOnly={activeEditor !== 'root'}
                        setActiveEditor={this.setActiveEditor}
                        activeEditor={activeEditor}
                        onChange={this.onChange}
                        editorState={editorState}
                    />
                    {!readOnly && enableAutosave && <EditorAutosave editorState={editorState} />}
                </Fragment>
            );
        }

        return null;
    }
}

export default EditorContainer;
